import React from 'react';
import { Form } from 'react-bootstrap';
import CustomRichTextEditor from '../CustomRichTextEditor';

// Example structured form for OBGYN (field_id = 6)
// Stores data as JSON object inside description, e.g. { category: 'ANC', g: 1, p: 0, a: 0 }
const ObgynNoteForm = ({ value, onChange, disabled = false }) => {
    const data = value || {};

    const handleCategoryChange = (category) => {
        if (!onChange) return;

        // Prepare resets based on target category
        let resets = {};
        if (category === 'ANC') {
            // Clear Gynae-only fields
            resets = {
                contraception: undefined,
                last_pap_smear: undefined,
            };
        } else if (category === 'Gynae') {
            // Clear ANC-only fields
            resets = {
                g: undefined,
                edd: undefined,
                ga: undefined,
            };
        }

        onChange({
            ...data,
            category,
            ...resets,
        });
    };

    const handleNumberChange = (field) => (e) => {
        if (!onChange) return;
        const raw = e.target.value;
        const numeric = raw === '' ? '' : Number(raw);
        onChange({
            ...data,
            [field]: isNaN(numeric) ? '' : numeric,
        });
    };

    const handleLmpChange = (e) => {
        if (!onChange) return;
        const lmp = e.target.value;
        let edd = '';
        let ga = '';

        // Only calculate EDD and GA for ANC patients
        if (lmp && data.category === 'ANC') {
            const lmpDate = new Date(lmp);
            if (!isNaN(lmpDate.getTime())) {
                const eddDate = new Date(lmpDate.getTime());
                eddDate.setDate(eddDate.getDate() + 280);
                const y = eddDate.getFullYear();
                const m = String(eddDate.getMonth() + 1).padStart(2, '0');
                const d = String(eddDate.getDate()).padStart(2, '0');
                edd = `${y}-${m}-${d}`;

                const today = new Date();
                const diffMs = today.getTime() - lmpDate.getTime();
                const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                const weeks = Math.floor(diffDays / 7);
                const days = diffDays % 7;
                if (weeks >= 0) {
                    ga = `${weeks}w ${days}d`;
                }
            }
        }

        onChange({
            ...data,
            lmp,
            edd,
            ga,
        });
    };

    const handleTextFieldChange = (field) => (e) => {
        if (!onChange) return;
        onChange({
            ...data,
            [field]: e.target.value,
        });
    };

    const handleUsReportChange = (e) => {
        if (!onChange) return;
        const value = e.target.value;
        onChange({
            ...data,
            us_report: value,
            us_report_other: value === 'other' ? (data.us_report_other || '') : '',
        });
    };

    const handleUsReportOtherChange = (e) => {
        if (!onChange) return;
        onChange({
            ...data,
            us_report_other: e.target.value,
        });
    };

    return (
        <div>
            {/* General clinical notes for both ANC and Gynae */}
            {(data.category === 'ANC' || data.category === 'Gynae') && (
                <div className="mt-3">
                    <Form.Group className="mb-2">
                        <Form.Label>Clinical notes</Form.Label>
                        <CustomRichTextEditor
                            value={data.clinical_notes || ''}
                            onChange={disabled ? undefined : (content) => {
                                if (!onChange) return;
                                onChange({
                                    ...data,
                                    clinical_notes: content,
                                });
                            }}
                            placeholder="Additional clinical notes"
                            style={{ minHeight: '120px' }}
                        />
                    </Form.Group>
                </div>
            )}
            <Form.Group className="mb-3">
                <Form.Label>Clinical Category</Form.Label>
                <div>
                    <Form.Check
                        inline
                        type="radio"
                        id="anc-option"
                        label="ANC"
                        name="category"
                        value="ANC"
                        checked={data.category === 'ANC'}
                        onChange={disabled ? undefined : () => handleCategoryChange('ANC')}
                    />
                    <Form.Check
                        inline
                        type="radio"
                        id="gynae-option"
                        label="Gynae"
                        name="category"
                        value="Gynae"
                        checked={data.category === 'Gynae'}
                        onChange={disabled ? undefined : () => handleCategoryChange('Gynae')}
                    />
                </div>
            </Form.Group>

            {/* ANC-specific fields */}
            {data.category === 'ANC' && (
                <div className="mt-3">
                    <Form.Label>Gravida / Para / Abortion</Form.Label>
                    <div className="d-flex" style={{ gap: '8px' }}>
                        <Form.Control
                            type="number"
                            min="0"
                            placeholder="G"
                            value={data.g ?? ''}
                            onChange={disabled ? undefined : handleNumberChange('g')}
                        />
                        <Form.Control
                            type="number"
                            min="0"
                            placeholder="P"
                            value={data.p ?? ''}
                            onChange={disabled ? undefined : handleNumberChange('p')}
                        />
                        <Form.Control
                            type="number"
                            min="0"
                            placeholder="A"
                            value={data.a ?? ''}
                            onChange={disabled ? undefined : handleNumberChange('a')}
                        />
                    </div>
                </div>
            )}

            {/* LMP is relevant to both ANC and Gynae; EDD/GA shown only for ANC */}
            {(data.category === 'ANC' || data.category === 'Gynae') && (
                <div className="mt-3">
                    <Form.Group className="mb-2">
                        <Form.Label>LMP</Form.Label>
                        <Form.Control
                            type="date"
                            value={data.lmp || ''}
                            onChange={disabled ? undefined : handleLmpChange}
                        />
                    </Form.Group>
                    {data.category === 'ANC' && (
                        <>
                            <Form.Group className="mb-2">
                                <Form.Label>EDD</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.edd || ''}
                                    readOnly
                                />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label>GA</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={data.ga || ''}
                                    readOnly
                                />
                            </Form.Group>
                        </>
                    )}
                </div>
            )}

            {/* Last U/S for both ANC and Gynae */}
            {(data.category === 'ANC' || data.category === 'Gynae') && (
                <div className="mt-3">
                    <Form.Group className="mb-2">
                        <Form.Label>Last U/S Report</Form.Label>
                        <Form.Select
                            value={data.us_report || ''}
                            onChange={disabled ? undefined : handleUsReportChange}
                        >
                            <option value="">Select</option>
                            <option value="normal">normal</option>
                            <option value="oligohydramnios">oligohydramnios</option>
                            <option value="polyhydramnios">polyhydramnios</option>
                            <option value="placenta previa">placent previa</option>
                            <option value="abnormal doppler">abnormal doppler</option>
                            <option value="other">other</option>
                        </Form.Select>
                    </Form.Group>
                    {data.us_report === 'other' && (
                        <Form.Group className="mb-2">
                            <Form.Label>Specify other</Form.Label>
                            <Form.Control
                                type="text"
                                value={data.us_report_other || ''}
                                onChange={disabled ? undefined : handleUsReportOtherChange}
                            />
                        </Form.Group>
                    )}
                </div>
            )}

            {/* Gynae-specific fields */}
            {data.category === 'Gynae' && (
                <div className="mt-3">
                    <Form.Group className="mb-2">
                        <Form.Label>Para (P)</Form.Label>
                        <Form.Control
                            type="number"
                            min="0"
                            placeholder="P"
                            value={data.p ?? ''}
                            onChange={disabled ? undefined : handleNumberChange('p')}
                        />
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label>Abortion (A)</Form.Label>
                        <Form.Control
                            type="number"
                            min="0"
                            placeholder="A"
                            value={data.a ?? ''}
                            onChange={disabled ? undefined : handleNumberChange('a')}
                        />
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label>Contraception</Form.Label>
                        <Form.Control
                            type="text"
                            value={data.contraception || ''}
                            onChange={disabled ? undefined : handleTextFieldChange('contraception')}
                            placeholder="Contraception method"
                        />
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label>Last Pap Smear</Form.Label>
                        <Form.Control
                            type="date"
                            value={data.last_pap_smear || ''}
                            onChange={disabled ? undefined : handleTextFieldChange('last_pap_smear')}
                        />
                    </Form.Group>
                </div>
            )}
        </div>
    );
};

export default ObgynNoteForm;
